import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";

/**
 * Manages the tutorial state for the currently logged-in user.
 *
 * - `firstLoginOpen` becomes true automatically when the user has not yet
 *   dismissed the tutorial (server state: tutorial_seen === false).
 * - `manualOpen` is controlled by the user (Help panel → "Tutorial wiederholen").
 * - Combined `open` = firstLoginOpen || manualOpen.
 */
export default function useTutorial() {
    const { user } = useAuth();
    const [firstLoginOpen, setFirstLoginOpen] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const open = firstLoginOpen || manualOpen;

    // Check server-side tutorial state once after login (auth is handled by `api` interceptor)
    useEffect(() => {
        let cancelled = false;
        async function check() {
            if (!user) return;
            try {
                const { data } = await api.get("/tutorial/state");
                if (cancelled || data.seen) return;
                // Wait until no other Radix dialog is open (e.g. an urgent announcement popup)
                // so the tour spotlight has the page to itself. Max wait: 5 minutes.
                const start = Date.now();
                while (
                    !cancelled &&
                    document.querySelector('[role="dialog"]') &&
                    Date.now() - start < 5 * 60_000
                ) {
                    await new Promise((r) => setTimeout(r, 500));
                }
                if (!cancelled) setFirstLoginOpen(true);
            } catch {
                /* silent */
            }
        }
        check();
        return () => { cancelled = true; };
    }, [user]);

    const dismiss = useCallback(async () => {
        try {
            await api.post("/tutorial/dismiss", {});
        } catch {/* ignore */}
        setFirstLoginOpen(false);
    }, []);

    const close = useCallback(() => {
        setFirstLoginOpen(false);
        setManualOpen(false);
    }, []);

    const replay = useCallback(() => {
        setManualOpen(true);
    }, []);

    return { open, dismiss, close, replay };
}
